import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import "./App.css";

// Import Shadcn components
import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Separator } from "./components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { toast } from "sonner";

// Icons from lucide-react
import {
  BookOpen,
  Calculator,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Clock,
  Trophy,
  Target,
  Brain,
  TrendingUp,
  User,
  CheckCircle,
  XCircle,
  ArrowRight,
  Lightbulb,
  Calendar,
  Star,
  CreditCard,
  Crown,
  Zap,
  Check,
  AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// Context for authentication
const AuthContext = React.createContext();

function useAuth() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });
      const { access_token, user: userData } = response.data;

      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      const responseData = error.response?.data;
      const responseContentType =
        error.response?.headers?.["content-type"] || "";

      if (
        typeof responseData === "string" &&
        (responseData.includes("Authentication Required") ||
          responseData.includes("Vercel Authentication") ||
          responseContentType.includes("text/html"))
      ) {
        return {
          success: false,
          error:
            "Login service is protected by Vercel Authentication. Disable Deployment Protection for API routes, then try again.",
        };
      }

      let errorMessage = "Login failed";
      if (typeof responseData === "string" && responseData.trim()) {
        errorMessage = responseData;
      } else if (typeof responseData?.detail === "string") {
        // Custom handling for user not found
        if (responseData.detail.includes("User not found")) {
          errorMessage = "Account does not exist. Please create an account.";
        } else {
          errorMessage = responseData.detail;
        }
      } else if (Array.isArray(responseData?.detail)) {
        errorMessage =
          responseData.detail
            .map((item) => item?.msg)
            .filter(Boolean)
            .join(", ") || errorMessage;
      } else if (typeof responseData?.message === "string") {
        errorMessage = responseData.message;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        username,
        email,
        password,
      });
      const { access_token, user: userData } = response.data;

      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      let errorMsg = "Registration failed";
      if (error.response) {
        // Show backend error details
        if (typeof error.response.data === "string") {
          errorMsg = error.response.data;
        } else if (error.response.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMsg = error.response.data.error;
        } else {
          errorMsg = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      return {
        success: false,
        error: errorMsg,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  return {
    user,
    token,
    login,
    register,
    logout,
    loading,
    refreshProfile: fetchUserProfile,
  };
}

// Authentication Component
function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const { login, register } = React.useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError("");

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(
          formData.username,
          formData.email,
          formData.password,
        );
      }

      if (!result.success) {
        setAuthError(result.error);
        toast.error(result.error);
      } else {
        setAuthError("");
        toast.success(`${isLogin ? "Login" : "Registration"} successful!`);
      }
    } catch (error) {
      setAuthError("An error occurred. Please try again.");
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-4">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          {/* Removed expired trial/upgrade message */}
        </div>

        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-center">
              {isLogin ? "Welcome Back" : "Get Started"}
            </CardTitle>
            <CardDescription className="text-center">
              {isLogin
                ? "Sign in to continue your GRE journey"
                : "Create your account to begin studying"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {authError && (
                <Alert variant="destructive" data-testid="auth-error-message">
                  <AlertDescription>{authError}</AlertDescription>
                </Alert>
              )}

              {!isLogin && (
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter your username"
                    value={formData.username}
                    onChange={(e) => {
                      setAuthError("");
                      setFormData((prev) => ({
                        ...prev,
                        username: e.target.value,
                      }));
                    }}
                    required
                    data-testid="username-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => {
                    setAuthError("");
                    setFormData((prev) => ({ ...prev, email: e.target.value }));
                  }}
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => {
                    setAuthError("");
                    setFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }));
                  }}
                  required
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={loading}
                data-testid="auth-submit-button"
              >
                {loading
                  ? "Please wait..."
                  : isLogin
                    ? "Sign In"
                    : "Create Account"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setAuthError("");
                  setIsLogin(!isLogin);
                }}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin
                  ? "Don't have an account? Sign up"
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// SubscriptionPage removed

// Dashboard Components
function Dashboard() {
  const { user, logout } = React.useContext(AuthContext);
  const [selectedSection, setSelectedSection] = useState("dashboard");
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, [user]);

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(`${API}/analytics/performance`);
      setPerformance(response.data);
    } catch (error) {
      console.error("Failed to fetch performance:", error);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "verbal", label: "Verbal Reasoning", icon: BookOpen },
    { id: "quantitative", label: "Quantitative Reasoning", icon: Calculator },
    { id: "analytical", label: "Analytical Writing", icon: FileText },
    { id: "practice-tests", label: "Practice Tests", icon: Clock },
    { id: "recommendations", label: "Study Plan", icon: Lightbulb },
  ];

  const handleSectionClick = (sectionId) => {
    setSelectedSection(sectionId);
  };

  const renderContent = () => {
    switch (selectedSection) {
      case "dashboard":
        return <DashboardContent performance={performance} loading={loading} />;
      case "verbal":
        return <SectionPractice section="verbal" title="Verbal Reasoning" />;
      case "quantitative":
        return (
          <SectionPractice
            section="quantitative"
            title="Quantitative Reasoning"
          />
        );
      case "analytical":
        return (
          <SectionPractice section="analytical" title="Analytical Writing" />
        );
      case "practice-tests":
        return <PracticeTests />;
      case "recommendations":
        return <StudyRecommendations />;
      default:
        return <DashboardContent performance={performance} loading={loading} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">
                GRE Study Master
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                <span>Welcome, {user?.username}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-gray-600 hover:text-gray-900"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Navigation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSectionClick(item.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                        selectedSection === item.id
                          ? "bg-blue-100 text-blue-700 font-medium"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                      data-testid={`nav-${item.id}`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="h-4 w-4" />
                        <span className="text-sm">{item.label}</span>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}

// Subscription Prompt Component
// SubscriptionPrompt removed

function DashboardContent({ performance, loading }) {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-200 rounded-lg h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const overallAccuracy = performance?.overall?.accuracy || 0;
  const totalQuestions = performance?.overall?.total_questions || 0;
  const correctAnswers = performance?.overall?.correct_answers || 0;

  return (
    <div className="space-y-6" data-testid="dashboard-content">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Progress</h2>
        <p className="text-gray-600">Track your GRE preparation journey</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">
                  Questions Answered
                </p>
                <p className="text-3xl font-bold">{totalQuestions}</p>
              </div>
              <Target className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">
                  Correct Answers
                </p>
                <p className="text-3xl font-bold">{correctAnswers}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">
                  Overall Accuracy
                </p>
                <p className="text-3xl font-bold">
                  {overallAccuracy.toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section Performance */}
      {performance?.by_section &&
        Object.keys(performance.by_section).length > 0 && (
          <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Performance by Section</CardTitle>
              <CardDescription>
                Your accuracy across different GRE sections
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(performance.by_section).map(([section, data]) => (
                <div key={section} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium capitalize">
                      {section} Reasoning
                    </span>
                    <span className="text-sm text-gray-600">
                      {data.accuracy.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={data.accuracy} className="h-2" />
                  <div className="text-xs text-gray-500">
                    {data.correct}/{data.total} questions correct
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

      {/* Recent Activity */}
      {performance?.recent_performance &&
        performance.recent_performance.length > 0 && (
          <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Your last 10 answered questions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {performance.recent_performance
                  .slice(0, 5)
                  .map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {activity.is_correct ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium capitalize">
                            {activity.section} Question
                          </p>
                          <p className="text-sm text-gray-600">
                            Completed in {activity.time_taken}s
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant={
                          activity.is_correct ? "default" : "destructive"
                        }
                        className={
                          activity.is_correct
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        {activity.is_correct ? "Correct" : "Incorrect"}
                      </Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
}

function SectionPractice({ section, title }) {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, [section]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/questions/${section}?limit=20`);
      setQuestions(response.data);
      if (response.data.length > 0) {
        setCurrentQuestion(response.data[0]);
        setStartTime(new Date());
      }
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const generateAIQuestion = async () => {
    try {
      setLoading(true);
      toast.success("Generating AI question...");

      const questionTypes = {
        verbal: [
          "text_completion",
          "sentence_equivalence",
          "reading_comprehension",
        ],
        quantitative: [
          "quantitative_comparison",
          "multiple_choice",
          "numeric_entry",
        ],
        analytical: ["analyze_issue", "analyze_argument"],
      };

      const randomType =
        questionTypes[section][
          Math.floor(Math.random() * questionTypes[section].length)
        ];

      const response = await axios.post(
        `${API}/questions/generate?section=${section}&question_type=${randomType}&difficulty=medium`,
      );

      setCurrentQuestion(response.data);
      setStartTime(new Date());
      toast.success("AI question generated successfully!");
    } catch (error) {
      console.error("Failed to generate question:", error);
      toast.error("Failed to generate AI question");
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!userAnswer.trim() || !currentQuestion) return;

    try {
      const endTime = new Date();
      const timeTaken = Math.floor((endTime - startTime) / 1000);

      const response = await axios.post(`${API}/answers`, {
        question_id: currentQuestion.id,
        user_answer: userAnswer.trim(),
        time_taken: timeTaken,
      });

      setResult(response.data);
      setShowResult(true);

      if (response.data.is_correct) {
        toast.success("Correct answer! Well done!");
      } else {
        toast.error("Incorrect. Review the explanation below.");
      }
    } catch (error) {
      console.error("Failed to submit answer:", error);
      toast.error("Failed to submit answer");
    }
  };

  const nextQuestion = () => {
    const currentIndex = questions.findIndex(
      (q) => q.id === currentQuestion.id,
    );
    if (currentIndex < questions.length - 1) {
      setCurrentQuestion(questions[currentIndex + 1]);
    } else {
      // Fetch more questions or loop back
      setCurrentQuestion(questions[0]);
    }

    setUserAnswer("");
    setShowResult(false);
    setResult(null);
    setStartTime(new Date());
  };

  if (loading && !currentQuestion) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`${section}-practice`}>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600">
            Practice questions to improve your skills
          </p>
        </div>
        <Button
          onClick={generateAIQuestion}
          disabled={loading}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          data-testid="generate-ai-question"
        >
          <Brain className="h-4 w-4 mr-2" />
          Generate AI Question
        </Button>
      </div>

      {currentQuestion ? (
        <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="capitalize">
                  {currentQuestion.question_type.replace("_", " ")} Question
                </CardTitle>
                <CardDescription>
                  Difficulty:{" "}
                  <Badge variant="outline" className="ml-1 capitalize">
                    {currentQuestion.difficulty}
                  </Badge>
                </CardDescription>
              </div>
              {currentQuestion.ai_generated && (
                <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <Brain className="h-3 w-3 mr-1" />
                  AI Generated
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentQuestion.passage && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">
                  Reading Passage:
                </h4>
                <p className="text-blue-800 leading-relaxed">
                  {currentQuestion.passage}
                </p>
              </div>
            )}

            <div>
              <h4 className="font-medium mb-3">Question:</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                {currentQuestion.question_text}
              </p>
            </div>

            {currentQuestion.options && (
              <div>
                <h4 className="font-medium mb-3">Options:</h4>
                <div className="space-y-2">
                  {currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => setUserAnswer(option)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                        userAnswer === option
                          ? "border-blue-500 bg-blue-50 text-blue-900"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                      disabled={showResult}
                      data-testid={`option-${index}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!currentQuestion.options && (
              <div>
                <Label htmlFor="answer">Your Answer:</Label>
                <Input
                  id="answer"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Enter your answer..."
                  disabled={showResult}
                  className="mt-2"
                  data-testid="answer-input"
                />
              </div>
            )}

            {!showResult ? (
              <Button
                onClick={submitAnswer}
                disabled={!userAnswer.trim() || loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="submit-answer"
              >
                Submit Answer
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <div className="space-y-4">
                <Alert
                  className={
                    result?.is_correct
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                  }
                >
                  <AlertDescription className="flex items-center space-x-2">
                    {result?.is_correct ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span
                      className={
                        result?.is_correct ? "text-green-800" : "text-red-800"
                      }
                    >
                      {result?.is_correct ? "Correct!" : "Incorrect"}
                      {!result?.is_correct && (
                        <span className="block mt-1">
                          Correct answer: {result?.correct_answer}
                        </span>
                      )}
                    </span>
                  </AlertDescription>
                </Alert>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Explanation:
                  </h4>
                  <p className="text-blue-800 leading-relaxed">
                    {currentQuestion.explanation}
                  </p>
                </div>

                <Button
                  onClick={nextQuestion}
                  className="w-full"
                  data-testid="next-question"
                >
                  Next Question
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Questions Available
            </h3>
            <p className="text-gray-600 mb-4">
              Generate an AI question to get started
            </p>
            <Button onClick={generateAIQuestion} disabled={loading}>
              <Brain className="h-4 w-4 mr-2" />
              Generate Question
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PracticeTests() {
  const [activeTest, setActiveTest] = useState(null);
  const [testResults, setTestResults] = useState(null);

  const startTest = async (section) => {
    try {
      const response = await axios.post(`${API}/test-sessions`, {
        section,
        time_limit: 30, // 30 minutes
      });

      setActiveTest(response.data);
      toast.success("Practice test started!");
    } catch (error) {
      console.error("Failed to start test:", error);
      toast.error("Failed to start practice test");
    }
  };

  return (
    <div className="space-y-6" data-testid="practice-tests">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Practice Tests
        </h2>
        <p className="text-gray-600">
          Take timed practice tests to simulate the real GRE experience
        </p>
      </div>

      {!activeTest ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              section: "verbal",
              title: "Verbal Reasoning Test",
              icon: BookOpen,
              duration: "30 minutes",
            },
            {
              section: "quantitative",
              title: "Quantitative Reasoning Test",
              icon: Calculator,
              duration: "35 minutes",
            },
            {
              section: "analytical",
              title: "Analytical Writing Test",
              icon: FileText,
              duration: "60 minutes",
            },
          ].map((test) => {
            const Icon = test.icon;
            return (
              <Card
                key={test.section}
                className="bg-white/60 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{test.title}</CardTitle>
                      <CardDescription>
                        <Clock className="h-3 w-3 inline mr-1" />
                        {test.duration}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => startTest(test.section)}
                    className="w-full"
                    data-testid={`start-${test.section}-test`}
                  >
                    Start Test
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Practice Test in Progress</CardTitle>
            <CardDescription>
              Time Limit: {activeTest.time_limit} minutes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Test feature is under development. Use individual section
                practice for now.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StudyRecommendations() {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/recommendations`);
      setRecommendations(response.data);
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
      toast.error("Failed to load study recommendations");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="study-recommendations">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Study Recommendations
        </h2>
        <p className="text-gray-600">
          AI-powered personalized study suggestions based on your performance
        </p>
      </div>

      {recommendations?.message ? (
        <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <Brain className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Get Personalized Recommendations
            </h3>
            <p className="text-gray-600 mb-6">{recommendations.message}</p>
            <div className="text-left max-w-md mx-auto">
              <h4 className="font-medium mb-3">General Study Tips:</h4>
              <ul className="space-y-2">
                {recommendations.recommendations?.map((rec, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Star className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {recommendations?.weak_areas && (
            <Card className="bg-red-50 border-red-200">
              <CardHeader>
                <CardTitle className="text-red-800 flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  Areas for Improvement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recommendations.weak_areas.map((area, index) => (
                    <Badge
                      key={index}
                      variant="destructive"
                      className="mr-2 mb-2"
                    >
                      {area}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {recommendations?.recommendations && (
            <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Lightbulb className="h-5 w-5 mr-2" />
                  Personalized Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {recommendations.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-blue-600">
                          {index + 1}
                        </span>
                      </div>
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {recommendations?.study_plan && (
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Your Study Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-800 leading-relaxed">
                  {recommendations.study_plan}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// Main App Component
function App() {
  const auth = useAuth();

  if (auth.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading GRE Study Master...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      <Router>
        <div className="App">
          <Routes>
            <Route
              path="/*"
              element={auth.token ? <Dashboard /> : <AuthPage />}
            />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
